import { AxiosRequestConfig } from 'axios';
import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { Status } from 'utils/customTypes';
import { SLICE_STATUS } from 'utils/constants';
import {
  BulkDeleteTasksResponse,
  bulkDeleteTasks as bulkDeleteTasksApi,
} from 'api/tasksList';
import { RootState, ThunkApi } from 'state/store';
import {
  selectProjectTasksExportState,
  selectFilteredSelectedTasksIds,
} from 'state/ProjectTasksList/export/exportSlice';
import { selectProjectTasksTableFilters } from 'state/ProjectTasksList/projectTasksListSlice';
import {
  setBulkActionsNotifications,
  setBulkActionOperationIdAndType,
  setBulkOperationInProgress,
} from '../bulkActionsSlice';
import {
  getBulkDeleteTasksUrl,
  deserializeBulkDeleteTasksResponse,
  trackBulkDeleteEvent,
} from './utils';
import {
  generateBulkDeleteNotifications,
  generateRejectedBulkDeleteNotifications,
} from '../notifications';

interface BulkDeleteState {
  status?: Status;
  error?: string;
}

const initialState: BulkDeleteState = {};

function bulkDeleteTasksCondition(
  thunkParameters: unknown,
  { getState }: { getState: () => RootState }
): boolean {
  const { status } = getState().projectTasksList.bulkActions.bulkDelete;
  return status !== SLICE_STATUS.LOADING;
}

interface BulkDeleteTasksParams {
  projectId: string;
  config?: AxiosRequestConfig;
}

export const bulkDeleteTasks = createAsyncThunk<
  BulkDeleteTasksResponse,
  BulkDeleteTasksParams,
  ThunkApi
>(
  'projectTasksList/bulkActions/bulkDelete/bulkDeleteTasks',
  async (
    params: BulkDeleteTasksParams,
    { getState, rejectWithValue, dispatch }
  ) => {
    try {
      dispatch(setBulkOperationInProgress(true));
      const state = getState() as RootState;
      const filters = selectProjectTasksTableFilters(state);
      const exportState = selectProjectTasksExportState(state);
      const filteredSelectedTasksIds = selectFilteredSelectedTasksIds(state);
      const url = getBulkDeleteTasksUrl({
        filters,
        exportState: {
          selectAll: exportState.selectAllTasks,
          excludedIds: exportState.excludedTasksIds,
          selectedIds: filteredSelectedTasksIds,
        },
        projectId: params.projectId,
      });
      const response = await bulkDeleteTasksApi(url, params.config);
      const deserializedResponse = await deserializeBulkDeleteTasksResponse(
        response
      );
      trackBulkDeleteEvent(state, deserializedResponse);
      dispatch(
        setBulkActionOperationIdAndType({
          id: deserializedResponse.operationId,
          type: 'delete',
        })
      );
      const notifications = generateBulkDeleteNotifications(
        deserializedResponse.successfulCount,
        deserializedResponse.failedCount,
        deserializedResponse.operationId
      );
      dispatch(setBulkActionsNotifications(notifications));
      dispatch(setBulkOperationInProgress(false));
      return deserializedResponse;
    } catch (error) {
      const notifications = generateRejectedBulkDeleteNotifications();
      dispatch(setBulkActionsNotifications(notifications));
      return rejectWithValue(error);
    }
  },
  {
    condition: bulkDeleteTasksCondition,
  }
);

const bulkDeleteSlice = createSlice({
  name: 'projectTasksList/bulkActions/bulkDelete',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(bulkDeleteTasks.pending, (state) => {
        state.status = SLICE_STATUS.LOADING;
      })
      .addCase(bulkDeleteTasks.fulfilled, (state, action) => {
        state.status = SLICE_STATUS.SUCCESS;
        state.error = undefined;
      })
      .addCase(bulkDeleteTasks.rejected, (state, action) => {
        state.status = SLICE_STATUS.FAILED;
        state.error = action.error.message;
      });
  },
});

export const selectBulkDeleteState = (state: RootState) =>
  state.projectTasksList.bulkActions.bulkDelete;

export const selectBulkDeleteLoading = createSelector(
  [selectBulkDeleteState],
  (bulkDelete: BulkDeleteState) => bulkDelete.status === SLICE_STATUS.LOADING
);

export default bulkDeleteSlice.reducer;
